/**
 * @fileoverview View mode content for patient details modal
 */
import React from "react";
import PropTypes from "prop-types";
import { intl } from "dmsbasereact";
import { Box, Grid, Chip, Typography } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import HomeIcon from "@mui/icons-material/Home";
import ContactsIcon from "@mui/icons-material/Contacts";
import PregnantWomanIcon from "@mui/icons-material/PregnantWoman";
import { DmsInfoCard } from "../../DmsDetailModal/DmsInfoCard";
import { DmsInfoField } from "../../DmsDetailModal/DmsInfoField";
import { PatientStatus, PatientStatusEnum } from "../../../constants/PatientStatusEnum";
import { calculateAge } from "../../../utils/calculateAge";
import { formatDate, formatCpf, formatCep } from "../../../utils/formatters";

/**
 * Get gender label from value
 */
const getGenderLabel = (genderValue) => {
  if (!genderValue) return "-";
  const upperGender = String(genderValue).toUpperCase();
  if (upperGender === "M") return intl.get("Male").d("Male");
  if (upperGender === "F") return intl.get("Female").d("Female");
  return genderValue;
};

/**
 * View mode content for displaying patient details
 */
export const ViewModeContent = ({ data }) => {
  const patient = data;

  const getStatusChip = () => {
    return <Chip label={PatientStatus.getText(patient.status)} color={PatientStatus.getColor(patient.status)} size="small" />;
  };

  const isFemale = String(patient.gender || "").toUpperCase() === "F";

  return (
    <Box sx={{ maxHeight: "60vh", overflowY: "auto" }}>
      {/* Basic Information */}
      <DmsInfoCard icon={<PersonIcon />} title={intl.get("BasicInfo").d("Basic Information")}>
        <Grid container spacing={2}>
          <DmsInfoField label={intl.get("Number").d("Number")} value={patient.patient_identifier} />
          <DmsInfoField label={intl.get("Name").d("Name")} value={patient.name} />
          <DmsInfoField label={intl.get("CPF").d("CPF")} value={patient.cpf} />
          <DmsInfoField label={intl.get("Gender").d("Gender")} value={getGenderLabel(patient.gender)} />
          <DmsInfoField label={intl.get("BirthDate").d("Birth Date")} value={formatDate(patient.birth_date)} />
          <DmsInfoField
            label={intl.get("Age").d("Age")}
            value={patient.birth_date ? `${calculateAge(patient.birth_date)} ${intl.get("Years").d("years")}` : "-"}
          />
          <Grid item xs={6} sm={4}>
            <Typography variant="body2" color="text.secondary">{intl.get("Status").d("Status")}</Typography>
            {getStatusChip()}
          </Grid>
          {patient.status === PatientStatusEnum.INACTIVE && patient.patient_status_reason && (
            <DmsInfoField
              label={intl.get("DeactivationReason").d("Deactivation Reason")}
              value={patient.patient_status_reason.name}
              xs={12}
              sm={8}
            />
          )}
          {patient.status === PatientStatusEnum.INACTIVE && patient.status_reason && (
            <DmsInfoField
              label={intl.get("CustomReason").d("Custom Reason")}
              value={patient.status_reason}
              xs={12}
            />
          )}
        </Grid>
      </DmsInfoCard>

      {/* Contact Information */}
      <DmsInfoCard icon={<ContactsIcon />} title={intl.get("ContactInfo").d("Contact Information")}>
        <Grid container spacing={2}>
          <DmsInfoField label={intl.get("Email").d("Email")} value={patient.email} />
          <DmsInfoField label={intl.get("Phone").d("Phone")} value={patient.phone} />
        </Grid>
      </DmsInfoCard>

      {/* Address Information */}
      {patient.address && (
        <DmsInfoCard icon={<HomeIcon />} title={intl.get("AddressInfo").d("Address Information")}>
          <Grid container spacing={2}>
            <DmsInfoField label={intl.get("ZipCode").d("Zip Code")} value={formatCep(patient.address.zip_code)} />
            <DmsInfoField label={intl.get("Address").d("Address")} value={patient.address.address} />
            <DmsInfoField label={intl.get("Number").d("Number")} value={patient.address.number} />
            <DmsInfoField label={intl.get("City").d("City")} value={patient.address.city} />
            <DmsInfoField label={intl.get("State").d("State")} value={patient.address.state} />
          </Grid>
        </DmsInfoCard>
      )}

      {/* Reproductive Health (Female only) */}
      {isFemale && (
        <DmsInfoCard icon={<PregnantWomanIcon />} title={intl.get("ReproductiveHealth").d("Reproductive Health")}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4}>
              <Typography variant="body2" color="text.secondary">{intl.get("RiksCategory").d("Risk Category")}</Typography>
              <Chip
                label={patient.has_pregnancy_potential ? intl.get("Yes").d("Yes") : intl.get("No").d("No")}
                color={patient.has_pregnancy_potential ? "warning" : "default"}
                size="small"
              />
            </Grid>
            {patient.contraceptiveMethod && (
              <DmsInfoField label={intl.get("ContraceptiveMethod").d("Contraceptive Method")} value={patient.contraceptiveMethod} />
            )}
            {patient.infertilityReason && (
              <DmsInfoField label={intl.get("InfertilityReason").d("Infertility Reason")} value={patient.infertilityReason} />
            )}
          </Grid>
        </DmsInfoCard>
      )}

      {/* Legal Responsible */}
      {patient.responsibleLegal && (
        <DmsInfoCard icon={<PersonIcon />} title={intl.get("ResponsibleLegalData").d("Responsible Legal Data")}>
          <Grid container spacing={2}>
            <DmsInfoField label={intl.get("Name").d("Name")} value={patient.responsibleLegal.name} />
            <DmsInfoField label={intl.get("CPF").d("CPF")} value={formatCpf(patient.responsibleLegal.cpf)} />
            <DmsInfoField label={intl.get("Phone").d("Phone")} value={patient.responsibleLegal.phone} />
            <DmsInfoField label={intl.get("Email").d("Email")} value={patient.responsibleLegal.email} />
            {patient.responsibleAddress && (
              <>
                <DmsInfoField label={intl.get("ZipCode").d("Zip Code")} value={formatCep(patient.responsibleAddress.zip_code)} />
                <DmsInfoField label={intl.get("Address").d("Address")} value={patient.responsibleAddress.address} />
                <DmsInfoField label={intl.get("Number").d("Number")} value={patient.responsibleAddress.number} />
                <DmsInfoField label={intl.get("City").d("City")} value={patient.responsibleAddress.city} />
                <DmsInfoField label={intl.get("State").d("State")} value={patient.responsibleAddress.state} />
              </>
            )}
          </Grid>
        </DmsInfoCard>
      )}
    </Box>
  );
};

ViewModeContent.propTypes = {
  data: PropTypes.object.isRequired,
};
